'use client'

import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { MdArrowBack, MdSave, MdInventory, MdSearch } from 'react-icons/md'
import {
    getBatch,
    createBatch,
    updateBatch,
    CreateBatchData,
    getSuppliers,
    Supplier,
} from '@/lib/api/stock/batch-api'
import { searchMedications, Medication } from '@/lib/api/stock/medication-api'

export default function BatchFormPage() {
    const navigate = useNavigate()
    const { id } = useParams()
    const isEditing = !!id

    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [suppliers, setSuppliers] = useState<Supplier[]>([])
    const [medicationSearch, setMedicationSearch] = useState('')
    const [medicationResults, setMedicationResults] = useState<Medication[]>([])
    const [selectedMedication, setSelectedMedication] =
        useState<Medication | null>(null)
    const [searchingMedication, setSearchingMedication] = useState(false)

    const [formData, setFormData] = useState<CreateBatchData>({
        medicationId: 0,
        batchNumber: '',
        expirationDate: '',
        initialQuantity: 0,
        unitCost: 0,
        invoiceNumber: '',
        location: '',
        notes: '',
    })

    useEffect(() => {
        loadSuppliers()
        if (isEditing) {
            loadBatch()
        }
    }, [id])

    useEffect(() => {
        const timer = setTimeout(() => {
            if (medicationSearch.length >= 2) {
                searchMedicationsHandler()
            } else {
                setMedicationResults([])
            }
        }, 300)
        return () => clearTimeout(timer)
    }, [medicationSearch])

    const loadSuppliers = async () => {
        try {
            const response = await getSuppliers()
            if (response.success) {
                setSuppliers(response.result)
            }
        } catch (error) {
            console.error('Erro ao carregar fornecedores:', error)
        }
    }

    const loadBatch = async () => {
        setLoading(true)
        try {
            const response = await getBatch(Number(id))
            if (response.success) {
                const batch = response.result
                setFormData({
                    medicationId: batch.medicationId,
                    batchNumber: batch.batchNumber,
                    expirationDate: batch.expirationDate.split('T')[0],
                    initialQuantity: batch.initialQuantity,
                    unitCost: batch.unitCost,
                    invoiceNumber: batch.invoiceNumber || '',
                    location: batch.location || '',
                    notes: batch.notes || '',
                    supplierId: batch.supplierId,
                })
                if (batch.medication) {
                    setSelectedMedication(batch.medication as any)
                }
            } else {
                toast.error(response.error || 'Erro ao carregar lote')
                navigate('/stock/batches')
            }
        } catch (error) {
            toast.error('Erro ao carregar lote')
            navigate('/stock/batches')
        } finally {
            setLoading(false)
        }
    }

    const searchMedicationsHandler = async () => {
        setSearchingMedication(true)
        try {
            const response = await searchMedications(medicationSearch)
            if (response.success) {
                setMedicationResults(response.result)
            }
        } catch (error) {
            console.error('Erro ao buscar medicamentos:', error)
        } finally {
            setSearchingMedication(false)
        }
    }

    const selectMedication = (medication: Medication) => {
        setSelectedMedication(medication)
        setFormData({ ...formData, medicationId: medication.id })
        setMedicationSearch('')
        setMedicationResults([])
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!formData.medicationId) {
            toast.error('Selecione um medicamento')
            return
        }

        if (!formData.batchNumber.trim()) {
            toast.error('Número do lote é obrigatório')
            return
        }

        if (!formData.expirationDate) {
            toast.error('Data de validade é obrigatória')
            return
        }

        if (formData.initialQuantity <= 0) {
            toast.error('Quantidade deve ser maior que zero')
            return
        }

        setSaving(true)
        try {
            const response = isEditing
                ? await updateBatch(Number(id), {
                      location: formData.location,
                      notes: formData.notes,
                  })
                : await createBatch(formData)

            if (response.success) {
                toast.success(
                    isEditing
                        ? 'Lote atualizado com sucesso'
                        : 'Entrada registrada com sucesso'
                )
                navigate('/stock/batches')
            } else {
                toast.error(response.error || 'Erro ao salvar lote')
            }
        } catch (error) {
            toast.error('Erro ao salvar lote')
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex items-center gap-4">
                <button
                    onClick={() => navigate('/stock/batches')}
                    className="btn btn-ghost btn-sm"
                >
                    <MdArrowBack className="text-xl" />
                </button>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-secondary to-secondary/70 flex items-center justify-center shadow-lg">
                        <MdInventory className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            {isEditing ? 'Editar Lote' : 'Nova Entrada'}
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {isEditing
                                ? 'Atualize os dados do lote'
                                : 'Registre uma entrada de medicamento'}
                        </p>
                    </div>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold mb-4">Medicamento</h2>
                    {!isEditing ? (
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Buscar Medicamento{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <div className="relative">
                                <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none z-10" />
                                <input
                                    type="text"
                                    className="input input-bordered w-full pl-10 focus:input-primary"
                                    placeholder="Digite o nome do medicamento..."
                                    value={medicationSearch}
                                    onChange={e =>
                                        setMedicationSearch(e.target.value)
                                    }
                                />
                                {searchingMedication && (
                                    <span className="absolute right-3 top-1/2 -translate-y-1/2 loading loading-spinner loading-sm"></span>
                                )}
                            </div>
                            {medicationResults.length > 0 && (
                                <div className="mt-2 border border-base-300 rounded-lg overflow-hidden max-h-60 overflow-y-auto">
                                    {medicationResults.map(med => (
                                        <div
                                            key={med.id}
                                            className="p-3 hover:bg-base-200 cursor-pointer border-b border-base-300 last:border-b-0"
                                            onClick={() =>
                                                selectMedication(med)
                                            }
                                        >
                                            <div className="font-semibold">
                                                {med.name}
                                            </div>
                                            {med.genericName && (
                                                <div className="text-sm text-base-content/60">
                                                    {med.genericName}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {selectedMedication && (
                                <div className="mt-3 p-3 bg-secondary/10 rounded-lg">
                                    <div className="font-semibold text-secondary">
                                        Selecionado: {selectedMedication.name}
                                    </div>
                                    {selectedMedication.genericName && (
                                        <div className="text-sm">
                                            {selectedMedication.genericName}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        selectedMedication && (
                            <div className="p-3 bg-base-200 rounded-lg">
                                <div className="font-semibold">
                                    {selectedMedication.name}
                                </div>
                                {selectedMedication.genericName && (
                                    <div className="text-sm text-base-content/60">
                                        {selectedMedication.genericName}
                                    </div>
                                )}
                            </div>
                        )
                    )}
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Dados do Lote
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Número do Lote{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                value={formData.batchNumber}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        batchNumber: e.target.value,
                                    })
                                }
                                disabled={isEditing}
                                required
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Data de Validade{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered w-full focus:input-primary"
                                value={formData.expirationDate}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        expirationDate: e.target.value,
                                    })
                                }
                                disabled={isEditing}
                                required
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Quantidade{' '}
                                    <span className="text-error">*</span>
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full focus:input-primary"
                                min="1"
                                value={formData.initialQuantity}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        initialQuantity:
                                            parseInt(e.target.value) || 0,
                                    })
                                }
                                disabled={isEditing}
                                required
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Custo Unitário (R$)
                                </span>
                            </label>
                            <input
                                type="number"
                                className="input input-bordered w-full focus:input-primary"
                                min="0"
                                step="0.01"
                                value={formData.unitCost}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        unitCost:
                                            parseFloat(e.target.value) || 0,
                                    })
                                }
                                disabled={isEditing}
                            />
                        </div>
                    </div>
                </div>

                <div className="bg-base-100 rounded-xl border border-base-300/50 p-6">
                    <h2 className="text-lg font-semibold mb-4">
                        Informações Adicionais
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Fornecedor
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full focus:select-primary"
                                value={formData.supplierId || ''}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        supplierId: e.target.value
                                            ? parseInt(e.target.value)
                                            : undefined,
                                    })
                                }
                                disabled={isEditing}
                            >
                                <option value="">Selecione...</option>
                                {suppliers.map(supplier => (
                                    <option
                                        key={supplier.id}
                                        value={supplier.id}
                                    >
                                        {supplier.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Número da Nota Fiscal
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                value={formData.invoiceNumber}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        invoiceNumber: e.target.value,
                                    })
                                }
                                disabled={isEditing}
                            />
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Localização
                                </span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full focus:input-primary"
                                placeholder="Ex: Prateleira A1"
                                value={formData.location}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        location: e.target.value,
                                    })
                                }
                            />
                        </div>
                        <div className="form-control w-full md:col-span-2">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Observações
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full focus:textarea-primary"
                                rows={3}
                                value={formData.notes}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        notes: e.target.value,
                                    })
                                }
                            />
                        </div>
                    </div>
                </div>

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => navigate('/stock/batches')}
                        className="btn btn-ghost"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-xl" />
                        )}
                        {isEditing ? 'Atualizar' : 'Registrar'}
                    </button>
                </div>
            </form>
        </div>
    )
}
